import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CONFIG } from 'config/config';
import routes from 'config/config/routes';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
	NavigationWrapper,
	Page,
	TargetsWrapper,
	Text,
	UiWrapper,
	View,
} from '@coinscrap/webapp-core';

import { ClientSimulationWrapper } from 'wrappers/customWrapper/ClientSimulationWrapper';

import { Wrapper } from 'layout/components/Body/Wrapper';
import Footer from 'layout/components/Footer/Footer';
import Header from 'layout/components/Header/Header';

import BackNextButton from 'components/BackNextButton/BackNextButton';
import { FixButton } from 'components/FixButton/FixButton';
import { Loader } from 'components/Loader/Loader';
import { MainModal } from 'components/MainModal/MainModal';

import { useGetError } from 'hooks/useGetError';
import { useInitialScrollToTop } from 'hooks/useInitialScrollToTop';

import { getCurrency } from 'helpers/currency';
import { downloadDoc } from 'helpers/downloadDoc';

import { AgentContext } from '../../contexts/agent';
import { useCoinscrapPageView } from '../../hooks/useCoinscrapPageView';
import { Capital } from './components/Capital';
import { CapitalOther } from './components/CapitalOther';
import { Packages } from './components/Packages';
import { useTarification } from './hooks/useTarification';
import { useTarificationError } from './hooks/useTarificationError';

export const SimulationBase = ({
	internalStep,
	targetType,
	target,
	user,
	institution,
	isB2B,
	editMode,
}) => {
	const { agent } = useContext(AgentContext);
	const { saveTargetProperty } = TargetsWrapper.use();
	const { updateSimulation } = ClientSimulationWrapper?.use() || {};
	const { navigateTo, goBack, replaceRoute } = NavigationWrapper.use();
	const { useHeader, useFooter, useLoading, openModal } = UiWrapper.use();

	const [loading, setLoading] = useState();

	useFooter(<Footer />, []);
	useHeader(<Header />, []);
	useLoading(loading, { loadingMessage: loading });
	useInitialScrollToTop();
	useCoinscrapPageView('Simulation');
	const getError = useGetError();

	// TARGET CREATION DATA
	const creationData = useMemo(
		() => target?.targetProperties?.find((x) => x.name === 'creationData')?.value,
		[target],
	);

	// ESTADO
	const [sectionTitle, setSectionTitle] = useState('');
	const [sectionSubtitle, setSectionSubtitle] = useState('');
	const [simulationStep, setSimulationStep] = useState(false);
	useEffect(() => {
		if (internalStep) {
			setSimulationStep(internalStep);
		} else {
			setSimulationStep(1);
		}
	}, [internalStep]);

	const capitalMaximum = useMemo(() => 200000, []);
	const capitalMinimum = useMemo(() => 20000, []);
	const capitalList = useMemo(() => [75000, 100000, 150000, 200000, 'Otros'], []);
	const [capital, setCapital] = useState(
		capitalList.some((cap) => cap === creationData?.price)
			? creationData?.price
			: !creationData?.price
			? 75000
			: 'Otros',
	);

	const [isValidCapital, setIsValidCapital] = useState(true);
	const [invalidCapitalReason, setInvalidCapitalReason] = useState(
		`El capital máximo asegurable es de ${getCurrency(capitalMaximum, 0)}`,
	);
	const [showKoLink, setShowKoLink] = useState(false);

	const [otherCapital, setOtherCapital] = useState(creationData?.price ?? 0);
	const [otherCapitalInputWidth, setOtherCapitalInputWidth] = useState(2);

	const [selectedPack, setSelectedPack] = useState(creationData?.selectedPack || undefined);

	const resized = useRef(false);
	const handleResize = () => {
		resized.current = true;
	};

	const [trigger, setTrigger] = useState(0);

	// TARIFICACIÓN
	const { loadingPacks, packages, resumePdf, policyDates } = useTarification(
		simulationStep,
		target.id,
		capital,
		otherCapital,
		resized, // Hay un listener en el core que cuando se hace un resize en la página cambia el estado y hace que se renderize todo de nuevo. Para evitarlo se ha configurado otro event listener aquí para evitar la llamada en useTarificartion.
		trigger,
		setTrigger,
		useTarificationError,
		agent,
	);

	useEffect(() => {
		setSelectedPack(undefined);
		return () => {};
	}, [packages]);

	const selectPack = (pack) => {
		setSelectedPack(pack);
		next(pack);
	};

	// NAVIGATION
	const next = async (nextPack) => {
		switch (simulationStep) {
			case 1:
				try {
					const targetPayload = {
						...creationData,
						price: capital !== 'Otros' ? capital : +otherCapital,
					};
					await saveTargetProperty(target.id, 'creationData', targetPayload);
					CONFIG.routing.activateProduct.nextStep2(navigateTo);
					return;
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-Simulation-Step1-KO',
					});
				}
				break;
			case 2:
				try {
					setLoading('Actualizando...');
					const targetPayload = {
						...creationData,
						selectedPack: nextPack ?? selectedPack,
						policyDates,
					};
					await saveTargetProperty(target.id, 'creationData', targetPayload);
					editMode
						? CONFIG.routing.simulation.returnToActivation(navigateTo)
						: CONFIG.routing.simulation.next(navigateTo);
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-Simulation-Step2-KO',
					});
				}
				break;
			default:
				setLoading(undefined);
				openModal(
					() => (
						<MainModal
							type="error"
							text={getError(
								new Error(
									`simulationStep no válido: simulationStep ${simulationStep}`,
								),
							)}
						/>
					),
					{
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-Simulation-defaultKO',
					},
				);
				break;
		}
	};

	const isDisabled = () => {
		switch (simulationStep) {
			case 1:
				return !isValidCapital;
			case 2:
				return !selectedPack?.modalityDescription || loadingPacks;
			default:
				return true;
		}
	};

	useEffect(() => {
		switch (simulationStep) {
			case 1:
				setSectionTitle('¿Qué capital quieres asegurar?');
				setSectionSubtitle(
					'Es el importe que recibirá el/los beneficiario/s para cubrir las necesidades económicas.',
				);
				break;
			case 2:
				setSectionTitle('Selecciona tus coberturas');
				if (policyDates?.policyEffectiveDate && policyDates?.policyExpirationDate) {
					setSectionSubtitle(
						`Periodo de cobertura de ${policyDates?.policyEffectiveDate} a ${policyDates?.policyExpirationDate}.`,
					);
				} else {
					setSectionSubtitle('');
				}
				break;
			default:
				setSectionTitle('Tarificación');
				break;
		}
		return () => {};
	}, [simulationStep, policyDates]);

	useEffect(() => {
		const findPeriodsAndDecimalsCapital = [...otherCapital.toString()].filter(
			(item) => item === '.' || item === ',',
		);
		setOtherCapitalInputWidth(
			otherCapital.toString().trim().length + 3 + findPeriodsAndDecimalsCapital.length * -0.6,
		);
		return () => {};
	}, [otherCapital]);

	useEffect(() => {
		console.log('📌 user', user);
		console.log('📌 creationData', creationData);
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (simulationStep) {
			console.log('👉 simulationStep', simulationStep);
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [simulationStep]);

	return (
		<Wrapper
			showTopSection
			step={1}
			title={sectionTitle}
			subtitle={sectionSubtitle}
			enlaceResumePdf={!isMobile && simulationStep === 2 && resumePdf ? resumePdf : undefined}
		>
			<View innerWrapperSt>
				{
					{
						1: (
							<>
								<Capital
									capital={capital}
									setCapital={setCapital}
									setIsValidCapital={setIsValidCapital}
									otherCapital={otherCapital}
									setOtherCapital={setOtherCapital}
									capitalMaximum={capitalMaximum}
									capitalMinimum={capitalMinimum}
									capitalList={capitalList}
								/>

								<CapitalOther
									capital={capital}
									isValidCapital={isValidCapital}
									setIsValidCapital={setIsValidCapital}
									setInvalidCapitalReason={setInvalidCapitalReason}
									otherCapital={otherCapital}
									setOtherCapital={setOtherCapital}
									capitalMaximum={capitalMaximum}
									capitalMinimum={capitalMinimum}
									otherCapitalInputWidth={otherCapitalInputWidth}
									setShowKoLink={setShowKoLink}
								/>
								{/* {!isValidCapital && (
									<CapitalInvalid
										invalidCapitalReason={invalidCapitalReason}
										showKoLink={showKoLink}
									/>
								)} */}
							</>
						),
						2: loadingPacks ? (
							<Loader type="regular" loadingText={loadingPacks} />
						) : !loadingPacks && packages.length > 0 ? (
							<Packages
								packages={packages}
								selectedPack={selectedPack}
								resumePdf={resumePdf}
								selectPack={selectPack}
							/>
						) : null,
					}[simulationStep]
				}
			</View>
			<BackNextButton
				marginTop={'50px'}
				marginBottom={simulationStep === 2 && isMobile ? 80 : undefined}
				//isBackShown={false}
				onClickBack={
					simulationStep === 1
						? editMode
							? () =>
									navigateTo(routes.activateProduct, {
										clearItems: ['internalStep', 'editMode'],
									})
							: () =>
									navigateTo(routes.simulationData, {
										queryParams: { internalStep: 2 },
									})
						: () => {
								navigateTo(routes.simulation, {
									queryParams: { internalStep: 1 },
								});
						  }
				}
				nextText={
					simulationStep === 2
						? CONFIG.components.backNextButton.defaultText.contract
						: undefined
				}
				nextColor={simulationStep === 2 ? CONFIG.colors.secondary : undefined}
				onClickNext={next}
				isNextDisabled={isDisabled()}
				isNextShown={simulationStep === 2 ? false : true}
			/>
		</Wrapper>
	);
};
